'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { NdrCompanyCard, NdrCompanyCardSkeleton } from '@/app_components/NDR/NdrCompanyCard';
import { NdrFaq } from '@/app_components/NDR/NdrFaq';
import { NdrFooter } from '@/app_components/NDR/NdrFooter';
import { NdrHeader } from '@/app_components/NDR/NdrHeader';
import { NdrHero } from '@/app_components/NDR/NdrHero';
import { NdrStickyBar } from '@/app_components/NDR/NdrStickyBar';
import { TableOfContents } from '@/app_components/NDR/TableOfContents';
import { ndrData } from '@/app_data/mock/ndrData';
import { detectTrafficSource, getNdrHomeUrl, isNdrLink } from '@/app_lib/utils/ndrUtils';
import { cn } from '@/app_lib/utils/utils';

interface NdrWrapperContentProps {
  children?: React.ReactNode;
  source?: 'google' | 'bing';
}

interface RetreaverCampaign {
  // biome-ignore lint/style/useNamingConvention: required by Retreaver
  auto_replace_numbers: () => void;
}

export const Top5Wrapper = ({ children, source }: NdrWrapperContentProps) => {
  const stickyTriggerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;

  const [stickyBarMounted, setStickyBarMounted] = useState(false);
  const [companyCardMounted, setCompanyCardMounted] = useState(false);

  const retreaverCampaign = useRef<RetreaverCampaign | null>(null);
  const retreaverWaiting = useRef<boolean>(false);

  const searchParams = useSearchParams();
  // Get all query parameters as a string
  const allQueryParamsString =
    searchParams?.toString() || (source === 'google' ? 'src=fortune.google.organic' : 'src=fortune.bing.organic');
  const isGoogleSource = detectTrafficSource(searchParams) === 'google' || source === 'google';
  const homeUrl = getNdrHomeUrl(isGoogleSource ? 'google' : 'bing');

  const data = isGoogleSource ? ndrData.landingPages.google : ndrData.landingPages.bing;
  const topNumber = 5;
  const enabledData = data.reviews.filter((review) => review.ctas.primary.url !== '');
  const orderedReviews = enabledData.sort((a, b) => a.ordinal - b.ordinal).slice(0, topNumber);
  const stickyBarData = enabledData.filter((item) => item.sticky === true)[0];

  // Initialize (once) & run Retreaver to replace phone numbers
  const runRetreaver = useCallback(() => {
    // Package campaign config and phone number replacement into a single function
    // Used either in place, or in a deferred fashion if Retreaver is not yet loaded
    const performAction = () => {
      if (retreaverCampaign.current === null) {
        window.Retreaver.configure({
          host: 'api.calltrackapi.com',
          prefix: 'https',
        });

        retreaverCampaign.current = new window.Retreaver.Campaign({
          // biome-ignore lint/style/useNamingConvention: required by Retreaver
          campaign_key: 'c0ff9c04926762b90d3201cb7444c95b',
        }) as RetreaverCampaign;
      }

      retreaverCampaign.current.auto_replace_numbers();
    };

    // If currently waiting, do nothing
    if (retreaverWaiting.current) {
      return;
    }

    // Set an interval if the script hasn't loaded yet; otherwise, perform action immediately
    if (window.Retreaver === undefined) {
      const retreaveInterval = 100;
      retreaverWaiting.current = true;
      const intvl = setInterval(() => {
        // Once loaded, clear flag, clear interval and perform the action
        // Subsequent calls will go through the immediate path
        if (window.Retreaver !== undefined) {
          retreaverWaiting.current = false;
          clearInterval(intvl);
          performAction();
        }
      }, retreaveInterval);
    } else {
      performAction();
    }
  }, []);

  // Initialize Retreaver only after both components are mounted
  // This ensures that the phone number replacement works correctly
  // as these components contain the phone numbers to be replaced.
  // Note: The Retreaver script is loaded in the layout.tsx file.
  useEffect(() => {
    if (!(stickyBarMounted && companyCardMounted)) {
      return;
    }

    runRetreaver();
  }, [stickyBarMounted, companyCardMounted, runRetreaver]);

  // Render NDR page
  return (
    <div
      className={cn(
        'ndr-page-wrapper flex flex-col layout-ndr-gap layout-sticky-safe-area',
        isGoogleSource ? 'is-google-source' : 'is-bing-source',
      )}
    >
      <div className='w-full'>
        <NdrHeader
          className='py-xs layout-safe-area'
          disclaimerText={data.disclosure.text}
          disclaimerTitle={data.disclosure.title}
          homeUrl={homeUrl}
        />
        <div className='bg-secondary'>
          <NdrHero
            data={data}
            isTop5={true}
            queryParams={allQueryParamsString}
          />
        </div>
      </div>

      <div className='w-full lg:container-ft lg:mx-auto px-lg'>
        <div className='grid layout-grid-gap md:grid-cols-[2fr_1fr]'>
          <div
            className='flex flex-col flex-1 layout-gap-xs'
            data-cy='ndr-company-cards'
          >
            {orderedReviews.map((review, index) => (
              <Suspense
                fallback={<NdrCompanyCardSkeleton isHighlighted={index === 0} />}
                key={review.companySlug || review.companyName}
              >
                <NdrCompanyCard
                  card={review}
                  isHighlighted={index === 0}
                  onMount={() => setCompanyCardMounted(true)}
                  onResize={runRetreaver}
                  queryParams={allQueryParamsString}
                />
              </Suspense>
            ))}
          </div>
          {/* Sticky Table of Contents */}
          <div className='block'>
            <div className='md:sticky top-4'>
              <TableOfContents
                queryParams={allQueryParamsString}
                reviews={orderedReviews}
                source={isGoogleSource ? 'google' : 'bing'}
              />
            </div>
          </div>
        </div>
      </div>
      <NdrFaq
        className='px-lg'
        faqs={data.faqs}
        title='FAQ'
      />
      <div
        className='absolute'
        ref={stickyTriggerRef}
      />
      <NdrStickyBar
        onMount={() => setStickyBarMounted(true)}
        queryParams={isNdrLink(data.primaryCta.url) ? allQueryParamsString : ''}
        review={stickyBarData}
        stickyButtonPhone={data.defaultPhoneNumber}
        triggerElement={stickyTriggerRef}
      />
      <NdrFooter footerContent={data.footer} />
      {children}
    </div>
  );
};
